<script setup lang="ts">
import { computed, onBeforeMount, ref } from 'vue';
import _ from 'lodash';
import { DateTime } from 'luxon';
import { getTextVariant } from '@/utils/stringUtils';
import { useClientCopyComposable } from './useClientCopyComposable';
import { useComponentComposable } from '@/components/useComponentComposable';
import { useModalComposable } from '@/views/composable';
import { useI18nLocalize } from '@/i18n/i18nLocalize';
import { useUserAuthorizationsStore } from '@/store/UserAuthorizationsStore';
import { useMyQuoteStore } from '@/store/MyQuoteStore';
import { useViewQuoteStore } from '@/store/ViewQuoteStore';
import { useToast } from 'primevue/usetoast';
import Button from 'primevue/button';
import AgencyInformation from '@/components/modal/clientCopy/AgencyInformation.vue';
import ClientCopyFlightDetail from '@/components/modal/clientCopy/ClientCopyFlightDetail.vue';
import ClientCopyHeader from '@/components/modal/clientCopy/ClientCopyHeader.vue';
import ClientCopyTourPricing from '@/components/modal/clientCopy/ClientCopyTourPricing.vue';
import HTMLEditor from '@/components/common/HTMLEditor.vue';
import LoadingSpinner from '@/components/LoadingSpinner.vue';

interface Props {
  show: boolean;
  quote: object;
}
const props = defineProps<Props>();

const data = computed(() => props.quote);
const { i18nLocale } = useI18nLocalize();
const { t, $t } = i18nLocale(data.value.lang);
const logo = ref();
const packageClone = ref<any>([]);
const totalRoom = ref(0);
const isEditPricing = ref<boolean[][][]>([]);
const isEdit = ref(false);
const isPriceValid = ref(true);
const isLoading = ref(false);
const toast = useToast();
const userAuthorizationsStore = useUserAuthorizationsStore();
const authorizations: any = userAuthorizationsStore.permissionByRole;
const { fallBackImage } = useComponentComposable();
const { formatHotelPromtion, flightHotelImage, semistarImage } = useClientCopyComposable(data.value?.lang, data.value?.currency);
const { handleOpenErrorMessage } = useModalComposable();
const myQuoteStore = useMyQuoteStore();
const viewQuoteStore = useViewQuoteStore();

const openDetailPackage = (link: string) => {
  location.href = link;
};

const handleSubmitClientCopy = async () => {
  if (isEdit.value) {
    toast.add({ severity: 'info', summary: '', detail: t('client_copy.save_price_before_submit'), life: 3000 });
    return;
  }
  if (!isPriceValid.value) {
    handleOpenErrorMessage(t('client_copy.new_price_bigger'));
    return;
  }
  isLoading.value = true;
  const quoteCopy = _.cloneDeep(props.quote);
  quoteCopy.urlLogo = logo.value || quoteCopy.urlLogo;
  quoteCopy.packages = quoteCopy?.packages?.map((pack: any, indexPackage: number) => {
    return packageClone.value[indexPackage];
  });
  await viewQuoteStore.handleSubmitClientCopy(quoteCopy);
  isLoading.value = false;
};

onBeforeMount(() => {
  let total = 0;
  data.value?.packages?.forEach((packageItem: any, indexPackage: number) => {
    total += packageItem?.numOfRooms;
    packageClone.value.push({ ...packageItem });
    isEditPricing.value[indexPackage] = [];
    packageItem?.hotels?.forEach((hotel: any, indexHotel: number) => {
      isEditPricing.value[indexPackage][indexHotel] = [];
      hotel.rooms?.forEach((_: any, indexRoom: number) => {
        isEditPricing.value[indexPackage][indexHotel][indexRoom] = false;
      });
    });
  });

  totalRoom.value = total;
});
</script>
<template>
  <Transition name="modal">
    <div v-if="show" class="client-copy">
      <div class="modal-mask">
        <LoadingSpinner v-if="isLoading" />
        <div class="relative modal-container" @click.stop>
          <div class="sticky top-0">
            <div class="absolute right-0 flex align-items-center justify-content-center w-3rem h-3rem border-round">
              <Button class="text-900" icon="pi pi-times" severity="success" text rounded @click="$emit('close')" />
            </div>
          </div>
          <div class="wrapper py-4 px-5">
            <div class="wrapper">
              <table class="w-full">
                <thead>
                  <tr>
                    <th class="hidden" v-for="time in 3" :key="time"></th>
                  </tr>
                </thead>
                <tbody>
                  <ClientCopyHeader v-model:logo="logo" :url="`${myQuoteStore?.getUrlUploadImg}?clientCopy=true`" :data="data" />
                  <AgencyInformation :data="data" :totalRoom="totalRoom" />
                </tbody>
              </table>
              <div v-for="(packageItem, indexP) in data?.packages" :key="indexP">
                <table class="w-full">
                  <thead>
                    <tr>
                      <th class="hidden" v-for="time in 3" :key="time"></th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td class="pt-3 flex" colspan="3">
                        <img alt="img" replace-type="media" :src="flightHotelImage" class="height-25px" />
                        <div class="text-4xl font-bold">
                          {{ `${$t('common.option')} ${indexP + 1}: ` }}
                          {{ `${$t('client_copy.adults_count', packageItem.adtNo)}, ${$t('client_copy.children_count', packageItem.chdNo)} | ` }}
                          {{ `${$t('client_copy.rooms_count', packageItem.numOfRooms)}` }}
                        </div>
                      </td>
                    </tr>
                    <tr>
                      <td class="text-2xl pt-1">
                        <span>{{ getTextVariant(data?.lang, packageItem.gate, packageItem.gateFr) }}</span> {{ $t('common.to_normal') }}
                        <span>{{ getTextVariant(data?.lang, packageItem.dest, packageItem.destFr) }}</span>
                      </td>
                    </tr>
                    <tr class="break-before">
                      <td colspan="3" class="pt-3 text-xl">{{ $t('common.flight_details') }}</td>
                    </tr>
                    <tr>
                      <td class="text-base">{{ `${$t('common.labels.departure_date')}: ${packageItem.flights[0].depDate}` }} | {{ `${$t('client_copy.return_date')}: ${packageItem.flights[packageItem.flights.length - 1].depDate}` }}</td>
                    </tr>
                    <tr>
                      <td colspan="3" class="pt-2">
                        <ClientCopyFlightDetail :flights="packageItem.flights" :lang="data?.lang" />
                      </td>
                    </tr>
                    <tr class="break-before">
                      <td colspan="3" class="pt-3 text-xl">{{ $t('worksheet.hotel_detail') }}</td>
                    </tr>
                    <tr>
                      <td class="text-base">
                        {{ `${$t('client_copy.check_in')}: ${DateTime.fromISO(packageItem.depDate).toFormat('dd-LLL-yyyy')} | ` }}
                        {{
                          `${$t('client_copy.check_out')}: ${DateTime.fromISO(packageItem.depDate)
                            .plus({
                              days: packageItem.duration
                            })
                            .toFormat('dd-LLL-yyyy')} | `
                        }}
                        <span class="font-bold"> {{ `${t('client_copy.hotel_duration')}: ${t('client_copy.nights_count', packageItem.duration)}` }}</span>
                      </td>
                    </tr>
                    <template v-for="(hotel, indexH) in packageItem?.hotels" :key="indexH">
                      <tr>
                        <td colspan="3">
                          <table>
                            <thead>
                              <tr>
                                <th class="hidden" v-for="time in 10" :key="time"></th>
                              </tr>
                            </thead>
                            <tbody>
                              <tr>
                                <td class="pt-2 vertical-align-top">
                                  <img :src="hotel?.imgUrl || fallBackImage" alt="" class="height-150px width-220px" />
                                </td>
                                <td class="pl-3">
                                  <ul class="no-bullets">
                                    <li class="mb-1 font-bold text-2xl">{{ hotel?.hotel }}</li>
                                    <li class="mb-1">
                                      <i v-for="(item, index) in parseInt(hotel?.overallRate / 2)" :key="index" class="pi mr-1 pi-star-fill text-2xl text-color-secondary"></i>
                                      <i v-if="+hotel?.overallRate % 2 > 0" class="text-color-secondary relative top-2px">
                                        <img :src="semistarImage" alt="star" />
                                      </i>
                                    </li>
                                    <li class="font-bold mb-3 text-sm">{{ formatHotelPromtion(hotel?.hotelPromotions) }}</li>
                                    <li>
                                      <p class="text-sm">{{ hotel?.description }}</p>
                                      <a href="javascript:" @click="openDetailPackage(hotel?.urlDetail)" class="text-hyperlink">{{ $t('worksheet.hotel_detail') }}</a>
                                    </li>
                                  </ul>
                                </td>
                              </tr>
                            </tbody>
                          </table>
                        </td>
                      </tr>
                      <ClientCopyTourPricing
                        :tourInclusions="''"
                        :rooms="hotel.rooms"
                        :data="data"
                        :optionIndex="indexP"
                        :tourIndex="indexH"
                        :isHotel="true"
                        v-model:isEdit="isEdit"
                        v-model:isPriceValid="isPriceValid"
                        v-model:isEditPricing="isEditPricing"
                      />
                      <tr>
                        <HTMLEditor class="mb-3 w-full mt-1" v-model.trim="hotel.comment" />
                      </tr>
                    </template>
                  </tbody>
                </table>
              </div>
            </div>
            <div class="action-submit text-left">
              <Button v-if="authorizations?.my_quote?.client_copy_edit" :label="$t('common.buttons.submit')" class="mt-5" @click="handleSubmitClientCopy()" />
            </div>
          </div>
        </div>
      </div>
    </div>
  </Transition>
</template>
